import type { MechanicDefinition, MechanicContext } from "./types";

type LengthTier = "ultra_short" | "short" | "medium" | "long" | "epic";

const TIER_CONFIG: Record<LengthTier, { label: string; words: string; cap: number; rule: string }> = {
  ultra_short: {
    label: "Ultra Short (~20-40 words)",
    words: "~20-40 words",
    cap: 2,
    rule: "Max **2 narration sentences**. One action, one result. Telegram-style. Zero description, zero atmosphere. Pure consequence.",
  },
  short: {
    label: "Short (~40-80 words)",
    words: "~40-80 words",
    cap: 4,
    rule: "Max **4 narration sentences**. One sensory detail, one consequence. No padding, no atmosphere stacking.",
  },
  medium: {
    label: "Medium (~80-140 words)",
    words: "~80-140 words",
    cap: 7,
    rule: "Max **7 narration sentences**. One tight paragraph. Setup → consequence → what's next. No filler.",
  },
  long: {
    label: "Long (~140-220 words)",
    words: "~140-220 words",
    cap: 10,
    rule: "Max **10 narration sentences**. 2 paragraphs max. Richer pacing, optional dialogue — every sentence earns its place.",
  },
  epic: {
    label: "Epic (~220-350 words)",
    words: "~220-350 words",
    cap: 14,
    rule: "Max **14 narration sentences**. 2-3 paragraphs. Full cinematic beats with atmosphere.",
  },
};

export const narratorProfile: MechanicDefinition = {
  id: "narrator_profile",
  name: "Narrator Profile",
  description: "Comprehensive control of the narrator's prose style, tone, description depth, formatting, and inner voice.",
  icon: "PenTool",
  category: "narrative",
  alwaysActive: true,
  aiHints: {
    purpose: "Defines writing style, mood, verbosity limits, and internal character monologue (Inner Voice).",
    whenToUse: "Always active. Replaces individual tone/style mechanics with one unified prompt block.",
    synergies: ["multilingual"],
    conflicts: [],
    prerequisites: [],
    tags: ["writing", "style", "prose", "narrator", "tone", "verbosity"],
    complexity: 2,
    exampleConfig: { style: "literary", length: "short" },
    stateVariables: ["inner_voice_last"],
  },
  configSchema: [
    {
      key: "length",
      label: "Response Length (Verbosity)",
      type: "select",
      options: Object.entries(TIER_CONFIG).map(([value, cfg]) => ({
        value,
        label: cfg.label,
        description: cfg.rule.replace(/\*\*/g, "").slice(0, 80),
      })),
      default: "short",
    },
    {
      key: "style",
      label: "Writing Style",
      type: "select",
      options: [
        { value: "literary", label: "Literary (rich metaphors, lyrical)" },
        { value: "pulp", label: "Pulp (action-forward, punchy)" },
        { value: "journalistic", label: "Journalistic (factual, crisp)" },
        { value: "gothic", label: "Gothic (dark, atmospheric)" },
        { value: "noir", label: "Noir (cynical, shadowy)" },
      ],
      default: "literary",
    },
    {
      key: "prose_density",
      label: "Prose Details & Density",
      type: "select",
      options: [
        { value: "minimal", label: "Minimal (key features only, sparse)" },
        { value: "moderate", label: "Moderate (balanced detail)" },
        { value: "lush", label: "Lush (exhaustive sensory portrait)" },
      ],
      default: "moderate",
    },
    {
      key: "humor",
      label: "Humor Level",
      type: "select",
      options: [
        { value: "none", label: "None (dead serious)" },
        { value: "dry", label: "Dry (subtle wit)" },
        { value: "moderate", label: "Moderate (occasional levity)" },
        { value: "comedic", label: "Comedic (humor is central)" },
      ],
      default: "dry",
    },
    {
      key: "persona",
      label: "Narrator Persona",
      type: "select",
      options: [
        { value: "invisible", label: "Invisible (no personality)" },
        { value: "sage", label: "Wise Sage (knowing, measured)" },
        { value: "trickster", label: "Trickster (playful, unreliable)" },
        { value: "companion", label: "Companion (friendly, causal)" },
        { value: "bard", label: "Bard (performative, dramatic)" },
      ],
      default: "invisible",
    },
    {
      key: "combat_description",
      label: "Combat Narration",
      type: "select",
      options: [
        { value: "mechanical", label: "Mechanical (outcomes)" },
        { value: "cinematic", label: "Cinematic (blow-by-blow)" },
        { value: "brutal", label: "Brutal (visceral, unflinching)" },
      ],
      default: "cinematic",
    },
    {
      key: "inner_voice_frequency",
      label: "Inner Voice Frequency",
      type: "select",
      options: [
        { value: "none", label: "Disabled" },
        { value: "rare", label: "Rare (pivotal moments only)" },
        { value: "moderate", label: "Moderate (key decisions)" },
        { value: "frequent", label: "Frequent (reacts to mostly everything)" },
      ],
      default: "moderate",
      description: "Controls how often the PC's internal monologue surfaces.",
    },
    {
      key: "inner_voice_focus",
      label: "Inner Voice Focus",
      type: "select",
      options: [
        { value: "immersive", label: "Immersive (emotional, personality-driven)" },
        { value: "quest_guidance", label: "Quest Guidance (hints toward objectives)" },
        { value: "tactical", label: "Tactical (assess threats)" },
      ],
      default: "immersive",
    },
    {
      key: "breaks_fourth_wall",
      label: "Fourth Wall Breaks",
      type: "boolean",
      default: false,
    },
  ],

  initializeState: () => ({
    inner_voice_last: null,
  }),

  systemPromptSection: (config) => {
    const style = config.style || "literary";
    const length = (config.length || "short") as LengthTier;
    const density = config.prose_density || "moderate";
    const humor = config.humor || "dry";
    const persona = config.persona || "invisible";
    const combatDesc = config.combat_description || "cinematic";
    const ivFreq = config.inner_voice_frequency || "moderate";
    const ivFocus = config.inner_voice_focus || "immersive";
    const fourthWall = config.breaks_fourth_wall ? "You may occasionally break the fourth wall." : "Never break the fourth wall.";
    
    const tier = TIER_CONFIG[length] || TIER_CONFIG.short;

    let ivPrompt = "";
    if (ivFreq !== "none") {
      ivPrompt = `\n**INNER VOICE MECHANIC**:
- The Player Character has an internal monologue (${ivFreq} frequency, ${ivFocus} focus).
- Express the PC's personal doubts, hunches, fears, or tactical thoughts in FIRST PERSON inside an \`[INNER_VOICE]...[/INNER_VOICE]\` block.
- Keep it concise (20-60 words).`;
    }

    return `## NARRATOR PROFILE & FORMATTING
**Voice & Tone**:
- Style: ${style}. Density: ${density}. Humor: ${humor}.
- Persona: ${persona}. ${fourthWall}
- Combat Narration: ${combatDesc}.

**Formatting Constraints (STRICT)**:
- Mode: **${length.toUpperCase()}** (${tier.words})
- ${tier.rule}
- **CRITICAL CAP = ${tier.cap} sentences** (excluding NPC dialogue and INNER_VOICE blocks). If you write more than ${tier.cap} narration sentences, rewrite it to be shorter before responding.${ivPrompt}`;
  },

  tools: [],
  widgets: [
    {
      id: "inner_voice_display",
      type: "inline",
      component: "InnerVoiceWidget",
      label: "Inner Voice",
      icon: "brain",
      priority: 5,
      visibility: "when_data",
      dataBindings: {
        lastThought: "variables.inner_voice_last",
      },
    },
  ],
};
